import { parseThreadActivity } from "@productlane/lib/src/thread-activity-schemas"

import type { TimelineEvent } from "./timeline-events"
import type { ReadonlyJSONValue } from "@rocicorp/zero"

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export type ActivityEvent = Extract<TimelineEvent, { type: "activity" }>

export type AgentBurstEvent = Extract<
	TimelineEvent,
	{ type: "agentActivityBurst" }
>

/** Predicate that decides whether two consecutive activity events should be grouped. */
export type AggregationCondition = (
	a: ActivityEvent,
	b: ActivityEvent,
) => boolean

/** A rule that matches events by type, checks conditions, and merges a group. */
export interface AggregationRule {
	eventType: string | string[]
	conditions: AggregationCondition[]
	aggregate: (events: ActivityEvent[]) => ActivityEvent | null
}

// ---------------------------------------------------------------------------
// Duration parser
// ---------------------------------------------------------------------------

const DURATION_UNITS: Record<string, number> = {
	s: 1_000,
	m: 60_000,
	h: 3_600_000,
	d: 86_400_000,
}

function parseDuration(duration: string): number {
	const match = duration.match(/^(\d+)(s|m|h|d)$/)
	if (!match) throw new Error(`Invalid duration: "${duration}"`)
	return Number(match[1]!) * DURATION_UNITS[match[2]!]!
}

// ---------------------------------------------------------------------------
// Built-in predicates
// ---------------------------------------------------------------------------

export const sameActor: AggregationCondition = (a, b) =>
	a.threadActivity.actorId != null &&
	a.threadActivity.actorId === b.threadActivity.actorId

export const sameEventType: AggregationCondition = (a, b) =>
	a.threadActivity.eventType === b.threadActivity.eventType

export function inTimeframe(duration: string): AggregationCondition {
	const ms = parseDuration(duration)
	return (a, b) =>
		Math.abs(b.threadActivity.createdAt - a.threadActivity.createdAt) <= ms
}

// A close by the agent and a reopen by a customer reply are two separate facts,
// so only state changes with the same cause collapse into one.
export const sameStateChangeSource: AggregationCondition = (a, b) =>
	payload(a).source === payload(b).source

export function samePayloadField(field: string): AggregationCondition {
	return (a, b) => {
		const aVal = (a.threadActivity.payload as Record<string, unknown>)[field]
		const bVal = (b.threadActivity.payload as Record<string, unknown>)[field]
		return aVal != null && aVal === bVal
	}
}

// ---------------------------------------------------------------------------
// Aggregation helpers
// ---------------------------------------------------------------------------

function payload(event: ActivityEvent): Record<string, unknown> {
	return event.threadActivity.payload as Record<string, unknown>
}

/** Creates an aggregate fn that takes `fromKey` from the first event and `toKey` from the last.
 *  Returns null (removing the group) if the first and last values are equal - the changes cancelled out. */
export function firstToLast(
	fromKey: string,
	toKey: string,
): (events: ActivityEvent[]) => ActivityEvent | null {
	return (events) => {
		const from = payload(events[0]!)[fromKey]
		const to = payload(events[events.length - 1]!)[toKey]

		if (from === to) return null

		return {
			type: "activity" as const,
			threadActivity: {
				...events[0]!.threadActivity,
				createdAt: events[events.length - 1]!.threadActivity.createdAt,
				payload: {
					[fromKey]: from,
					[toKey]: to,
				} as unknown as ReadonlyJSONValue,
			},
		}
	}
}

// ---------------------------------------------------------------------------
// Default rules
// ---------------------------------------------------------------------------

export const defaultActivityRules: AggregationRule[] = [
	{
		eventType: "priority_changed",
		conditions: [sameActor, sameEventType, inTimeframe("3m")],
		aggregate: firstToLast("oldPriority", "newPriority"),
	},
	{
		eventType: "status_changed",
		conditions: [sameActor, sameEventType, inTimeframe("3m")],
		aggregate: firstToLast("oldStatus", "newStatus"),
	},
	{
		eventType: ["assignee_changed", "self_assigned"],
		conditions: [sameActor, inTimeframe("3m")],
		aggregate: (events) => {
			// self_assigned keeps the new assignee in `assigneeId`; a null old
			// assignee means the thread was unassigned, not that nothing changed.
			const oldId = payload(events[0]!).oldAssigneeId ?? null
			const lastEvent = events[events.length - 1]!
			const last = payload(lastEvent)
			const newId =
				(lastEvent.threadActivity.eventType === "self_assigned"
					? last.assigneeId
					: last.newAssigneeId) ?? null
			if (oldId === newId) return null
			return {
				type: "activity" as const,
				threadActivity: {
					...events[0]!.threadActivity,
					createdAt: events[events.length - 1]!.threadActivity.createdAt,
					eventType: "assignee_changed",
					payload: {
						oldAssigneeId: oldId,
						newAssigneeId: newId,
					} as unknown as ReadonlyJSONValue,
					newAssigneeName:
						events[events.length - 1]!.threadActivity.newAssigneeName,
				},
			}
		},
	},
	{
		eventType: ["thread_marked_done", "thread_reopened"],
		conditions: [sameActor, sameStateChangeSource, inTimeframe("1m")],
		aggregate: (events) => {
			const last = events[events.length - 1]!
			// If first and last are the same type, the state didn't change - keep last
			// If they differ (done→reopened or reopened→done), they cancel out - remove
			if (events[0]!.threadActivity.eventType !== last.threadActivity.eventType)
				return null
			return {
				type: "activity" as const,
				threadActivity: {
					...last.threadActivity,
				},
			}
		},
	},
	{
		eventType: ["issue_linked", "issue_unlinked"],
		conditions: [sameActor, samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => {
			const first = events[0]!
			const last = events[events.length - 1]!
			// If they cancel out (linked then unlinked, or vice versa), remove
			if (first.threadActivity.eventType !== last.threadActivity.eventType)
				return null
			return {
				type: "activity" as const,
				threadActivity: { ...last.threadActivity },
			}
		},
	},
	{
		eventType: ["project_linked", "project_unlinked"],
		conditions: [sameActor, samePayloadField("projectId"), inTimeframe("5m")],
		aggregate: (events) => {
			const first = events[0]!
			const last = events[events.length - 1]!
			if (first.threadActivity.eventType !== last.threadActivity.eventType)
				return null
			return {
				type: "activity" as const,
				threadActivity: { ...last.threadActivity },
			}
		},
	},
	{
		eventType: ["tag_linked", "tag_unlinked"],
		conditions: [sameActor, samePayloadField("tagId"), inTimeframe("5m")],
		aggregate: (events) => {
			const first = events[0]!
			const last = events[events.length - 1]!
			if (first.threadActivity.eventType !== last.threadActivity.eventType)
				return null
			return {
				type: "activity" as const,
				threadActivity: { ...last.threadActivity },
			}
		},
	},
	{
		eventType: "thread_snoozed",
		conditions: [sameActor, sameEventType, inTimeframe("5m")],
		aggregate: (events) => {
			// Keep only the last snooze (the final chosen time)
			return {
				type: "activity" as const,
				threadActivity: {
					...events[events.length - 1]!.threadActivity,
				},
			}
		},
	},
	{
		eventType: "linked_issue_planned",
		conditions: [samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "linked_issue_started",
		conditions: [samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "linked_issue_completed",
		conditions: [samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "linked_issue_cancelled",
		conditions: [samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "linked_project_completed",
		conditions: [samePayloadField("projectId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "linked_project_cancelled",
		conditions: [samePayloadField("projectId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: ["sla_policy_set", "sla_policy_removed"],
		conditions: [sameActor, inTimeframe("3m")],
		aggregate: (events) => {
			const last = events[events.length - 1]!
			// If it ends on removed and started without an SLA, cancel out
			if (
				last.threadActivity.eventType === "sla_policy_removed" &&
				events[0]!.threadActivity.eventType === "sla_policy_set"
			)
				return null
			return {
				type: "activity" as const,
				threadActivity: {
					...last.threadActivity,
					createdAt: last.threadActivity.createdAt,
				},
			}
		},
	},
	{
		eventType: "pr_opened",
		conditions: [sameEventType, samePayloadField("url"), inTimeframe("30m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "pr_merged",
		conditions: [sameEventType, samePayloadField("url"), inTimeframe("30m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "pr_closed",
		conditions: [sameEventType, samePayloadField("url"), inTimeframe("30m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "coding_agent_finished",
		conditions: [sameEventType, samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
	{
		eventType: "coding_agent_failed",
		conditions: [sameEventType, samePayloadField("issueId"), inTimeframe("5m")],
		aggregate: (events) => ({
			type: "activity" as const,
			threadActivity: {
				...events[events.length - 1]!.threadActivity,
			},
		}),
	},
]

// ---------------------------------------------------------------------------
// Aggregation engine
// ---------------------------------------------------------------------------

function matchesEventType(
	ruleType: string | string[],
	eventType: string,
): boolean {
	return Array.isArray(ruleType)
		? ruleType.includes(eventType)
		: ruleType === eventType
}

function findRule(
	rules: AggregationRule[],
	eventType: string,
): AggregationRule | undefined {
	return rules.find((r) => matchesEventType(r.eventType, eventType))
}

export function aggregateActivityEvents(
	events: ActivityEvent[],
	rules: AggregationRule[] = defaultActivityRules,
	// A group must not span a message, or the merged event jumps below a reply
	// that was sent before part of it.
	opts: { splitAt?: readonly number[] } = {},
): ActivityEvent[] {
	const splitAt = opts.splitAt ?? []
	const consumed = new Set<number>()
	const groups: Array<{
		indices: number[]
		aggregated: ActivityEvent | null
	}> = []

	for (let i = 0; i < events.length; i++) {
		if (consumed.has(i)) continue
		const event = events[i]!
		const rule = findRule(rules, event.threadActivity.eventType)
		if (!rule) continue

		const groupIndices: number[] = [i]
		let lastInGroup = event

		for (let j = i + 1; j < events.length; j++) {
			if (consumed.has(j)) continue
			const next = events[j]!
			if (!matchesEventType(rule.eventType, next.threadActivity.eventType))
				continue
			if (!rule.conditions.every((cond) => cond(lastInGroup, next))) continue
			if (
				splitsBetween(
					splitAt,
					lastInGroup.threadActivity.createdAt,
					next.threadActivity.createdAt,
				)
			)
				continue
			groupIndices.push(j)
			lastInGroup = next
		}

		if (groupIndices.length > 1) {
			const groupEvents = groupIndices.map((idx) => events[idx]!)
			const aggregated = rule.aggregate(groupEvents)
			groups.push({ indices: groupIndices, aggregated })
			for (const idx of groupIndices) consumed.add(idx)
		}
	}

	// Rebuild: emit non-consumed events in place, emit aggregated at last index of group
	const lastIndexToGroup = new Map<number, (typeof groups)[number]>()
	for (const group of groups) {
		lastIndexToGroup.set(group.indices[group.indices.length - 1]!, group)
	}

	const result: ActivityEvent[] = []
	for (let i = 0; i < events.length; i++) {
		if (!consumed.has(i)) {
			result.push(events[i]!)
		} else {
			const group = lastIndexToGroup.get(i)
			if (group?.aggregated) result.push(group.aggregated)
		}
	}

	return result
}

// ---------------------------------------------------------------------------
// Agent action bursts
// ---------------------------------------------------------------------------

export const AGENT_BURST_MIN_EVENTS = 3
export const AGENT_BURST_WINDOW = "3m"

/** True when the Productlane Agent authored this activity, not a teammate and not the system. */
export function isAgentAuthoredActivity(event: ActivityEvent): boolean {
	const { eventType, payload } = event.threadActivity
	// A null actorId means "not a teammate", which covers system events too, so
	// only an explicit marker in the payload can identify the agent.
	if (eventType !== "custom_data_action_run") return false
	try {
		const parsed = parseThreadActivity(eventType, payload)
		return (
			parsed.eventType === "custom_data_action_run" &&
			parsed.payload.actor === "agent"
		)
	} catch {
		return false
	}
}

const withinBurstWindow = inTimeframe(AGENT_BURST_WINDOW)

function splitsBetween(
	splitAt: readonly number[],
	from: number,
	to: number,
): boolean {
	return splitAt.some((at) => at > from && at <= to)
}

function toBurst(events: ActivityEvent[]): AgentBurstEvent {
	const newest = events[events.length - 1]!
	return {
		type: "agentActivityBurst",
		burst: {
			id: `agent-burst-${newest.threadActivity.id}`,
			createdAt: newest.threadActivity.createdAt,
			firstCreatedAt: events[0]!.threadActivity.createdAt,
			events,
		},
	}
}

/** Collapses each run of agent-authored activities into one burst event. */
export function collapseAgentActivityBursts(
	events: ActivityEvent[],
	opts: { splitAt?: readonly number[] } = {},
): (ActivityEvent | AgentBurstEvent)[] {
	const splitAt = opts.splitAt ?? []
	const result: (ActivityEvent | AgentBurstEvent)[] = []
	let run: ActivityEvent[] = []

	const flush = () => {
		if (run.length >= AGENT_BURST_MIN_EVENTS) result.push(toBurst(run))
		else result.push(...run)
		run = []
	}

	for (const event of events) {
		if (!isAgentAuthoredActivity(event)) {
			flush()
			result.push(event)
			continue
		}
		const previous = run[run.length - 1]
		if (
			previous &&
			(!withinBurstWindow(previous, event) ||
				splitsBetween(
					splitAt,
					previous.threadActivity.createdAt,
					event.threadActivity.createdAt,
				))
		) {
			flush()
		}
		run.push(event)
	}
	flush()

	return result
}

// ---------------------------------------------------------------------------
// Routine runs
// ---------------------------------------------------------------------------

export type RoutineRunEvent = Extract<TimelineEvent, { type: "routineRun" }>

/** The run id an activity belongs to, when a routine produced it. */
export function routineRunIdOf(event: ActivityEvent): string | null {
	const { eventType, payload } = event.threadActivity
	try {
		const parsed = parseThreadActivity(eventType, payload)
		if (parsed.eventType === "routine_run") return parsed.payload.runId
		const marker = (parsed.payload as { routineRunId?: string }).routineRunId
		return marker ?? null
	} catch {
		return null
	}
}

/**
 * Folds each routine's own row together with the changes it made, so a run
 * reads as one entry rather than a scattered handful. A routine_note keeps its
 * own row: a translation is content a teammate reads, not a status line.
 */
export function collapseRoutineRuns(
	events: (ActivityEvent | AgentBurstEvent)[],
): (ActivityEvent | AgentBurstEvent | RoutineRunEvent)[] {
	const runs = new Map<string, RoutineRunEvent>()
	const result: (ActivityEvent | AgentBurstEvent | RoutineRunEvent)[] = []

	for (const event of events) {
		if (event.type !== "activity") {
			result.push(event)
			continue
		}
		if (event.threadActivity.eventType === "routine_note") {
			result.push(event)
			continue
		}
		const runId = routineRunIdOf(event)
		if (!runId) {
			result.push(event)
			continue
		}

		const isRunRow = event.threadActivity.eventType === "routine_run"
		const existing = runs.get(runId)
		if (!existing) {
			const run: RoutineRunEvent = {
				type: "routineRun",
				run: {
					id: `routine-run-${runId}`,
					createdAt: event.threadActivity.createdAt,
					runActivity: event,
					events: isRunRow ? [] : [event],
				},
			}
			runs.set(runId, run)
			result.push(run)
			continue
		}
		if (isRunRow) {
			existing.run.runActivity = event
			existing.run.createdAt = event.threadActivity.createdAt
		} else {
			existing.run.events.push(event)
		}
	}

	return result
}
